import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { trackUserAction } from "@/utils/tracking";

const ArchivedNewsletter = () => {
  const { data: files, isLoading } = useQuery({
    queryKey: ["archived-newsletter-files"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("downloadable_files")
        .select("*")
        .in("category", ["archived_newsletter", "pitch_deck"]) 
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const newsletters = (files || []).filter((f) => f.category === "archived_newsletter");
  const sortedNewsletters = [...newsletters].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  
  // Group files by year
  const groupedByYear = sortedNewsletters.reduce((acc, file) => {
    const year = new Date(file.created_at).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(file);
    return acc;
  }, {} as Record<number, typeof sortedNewsletters>);
  
  const years = Object.keys(groupedByYear).map(Number).sort((a, b) => b - a);

  const handleDownload = async (file: any) => {
    await trackUserAction("download", { file_name: file.title });
    window.open(file.file_url, "_blank");
  };

  return (
    <main className="max-w-5xl mx-auto space-y-8">
      <header className="bg-card rounded-lg p-6">
        <h1 className="text-3xl font-bold text-primary">Archived Newsletter & Pitch Decks</h1>
        <p className="text-muted-foreground mt-1">Browse and download archived investor updates and pitch decks.</p>
      </header>

      <section aria-labelledby="newsletters">
        <Card>
          <CardHeader>
            <CardTitle id="newsletters">Archived Files</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : newsletters.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No newsletters available</div>
            ) : (
              <div className="space-y-8">
                {years.map((year) => (
                  <div key={year} className="space-y-4">
                    <h3 className="text-xl font-semibold text-primary border-b border-border pb-2">
                      {year}
                    </h3>
                    <div className="grid gap-4">
                      {groupedByYear[year].map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                          <div className="flex-1">
                            <h4 className="font-semibold">{file.title}</h4>
                            {file.description && (
                              <p className="text-sm text-muted-foreground mb-1">{file.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {new Date(file.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </p>
                          </div>
                          <Button variant="outline" onClick={() => handleDownload(file)} className="flex items-center gap-2 ml-4">
                            <Download className="h-4 w-4" /> Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

    </main>
  );
};

export default ArchivedNewsletter;